import { useSelector } from 'react-redux';
import { AppStoreState } from 'types';

import React from 'react';
import { Box, Typography, Paper, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import AttemptFiles from './AttemptFiles';

import { translate } from 'translations/translator';

const getTranslation = (key, options?) => translate('assignmentTracker', key, options);

interface AssignmentSectionProps {
  title: string;
  activities: any[];
}

export const AssignmentAttemptsContent = (): React.ReactElement | null => {
  const theme = useTheme();
  const classes = useStyles(theme)();

  const assignmentAttempt = useSelector(({ entity }: AppStoreState) => entity.assignmentAttempt);

  const { assignment, files = [] } = assignmentAttempt;

  return (
    <Box p={4}>
      <Box p={2}>
        {assignment.sections.map((section, idx) => (
          <Paper
            key={`assignment-section-${idx}`}
            elevation={3}
            className={classes.sectionContainer}
          >
            <AssignmentSection
              key={section.id}
              title={section.title}
              activities={section.activities}
            />
          </Paper>
        ))}
        {files.length > 0 && (
          <Paper elevation={3}>
            <AttemptFiles files={files} />
          </Paper>
        )}
      </Box>
    </Box>
  );
};

const Activity: React.FC<{ activity: any }> = ({ activity }) => {
  const theme = useTheme();
  const classes = useStyles(theme)();
  const workbooks = useSelector(({ entity }: AppStoreState) => entity.workbooks);

  let activityType;
  let title = getTranslation('activity');

  if (activity.clickAway) {
    activityType = getTranslation('activityTypeClickAway');
    title = activity.clickAway.title;
  } else if (activity.customContent) {
    activityType = getTranslation('activityTypeCustomContent');
    title = activity.customContent.title;
  } else if (activity.page) {
    activityType = getTranslation('activityTypeWorkbook');
    const workbook = workbooks.find(book => activity.page.bookId === book.id);
    title = getTranslation('workbookPage', {
      workbookName: workbook?.title ?? getTranslation('workbook'),
      pageNumber: activity.page.pageNumber
    });
  } else if (activity.resource) {
    activityType = activity.resource.type.title;
    title = activity.resource.title;
  }

  return (
    <Box key={activity.id} my={1} display="flex" alignItems="center">
      <Typography variant="h6" className={classes.activityHeader}>
        {title}
      </Typography>
      {activityType && (
        <Box ml={2}>
          <Typography variant="body1" className={classes.activityType}>
            <span className={classes.activityTypeLabel}>{getTranslation('activityType')}:</span>
            {activityType}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

const AssignmentSection: React.FC<AssignmentSectionProps> = ({ title, activities }) => {
  const theme = useTheme();
  const classes = useStyles(theme)();

  return (
    <Box p={3} mb={2}>
      <Typography variant="h4" className={classes.sectionHeader}>
        {title} ({activities.length})
      </Typography>
      {activities.map(activity => (
        <Activity key={activity.id} activity={activity} />
      ))}
    </Box>
  );
};

const useStyles = theme =>
  makeStyles(() => ({
    titleHeader: () => ({
      paddingBottom: theme.spacing(2)
    }),
    activityHeader: {
      fontWeight: '400 !important'
    },
    sectionHeader: () => ({
      fontWeight: '400 !important',
      paddingBottom: theme.spacing(2)
    }),
    sectionContainer: () => ({
      marginBottom: theme.spacing(4),
      marginTop: theme.spacing(4)
    }),
    activityTypeLabel: () => ({
      fontWeight: theme.typography.fontWeightBold,
      marginRight: theme.spacing(1)
    }),
    activityType: () => ({
      color: theme.palette.grey[600]
    })
  }));
